import { LitElement, html, nothing, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { MAX_PLAYERS } from '@juegazo/shared';

/**
 * Índices de los nombres repetidos (comparación sin mayúsculas y sin
 * espacios). Es la ÚNICA implementación de la regla "no puede haber nombres
 * repetidos": la usan la hoja de jugadores (para marcar los inputs) y la
 * pantalla de inicio (para decidir si se puede empezar).
 */
export const findDuplicateIndices = (names: readonly string[]): Set<number> => {
  const trimmed = names.map((name) => name.trim());
  const counts = new Map<string, number>();
  trimmed.forEach((name) => {
    if (!name) return;
    const key = name.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  const dupes = new Set<number>();
  trimmed.forEach((name, i) => {
    if (!name) return;
    if ((counts.get(name.toLowerCase()) ?? 0) > 1) dupes.add(i);
  });
  return dupes;
};

@customElement('jgz-players-config')
export class JgzPlayersConfig extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Array }) players: string[] = [];

  private playersSheetOpen = false;
  private workingPlayers: string[] | null = null;
  private overlayHost: HTMLDivElement | null = null;

  private getOverlayHost(): HTMLDivElement {
    if (!this.overlayHost) {
      this.overlayHost = document.createElement('div');
      document.body.appendChild(this.overlayHost);
    }
    return this.overlayHost;
  }

  private syncOverlay() {
    const host = this.getOverlayHost();
    render(
      this.playersSheetOpen
        ? html`
            <div
              class="bottom-sheet-backdrop"
              @click=${() => (this.playersSheetOpen = false)}
            ></div>
            ${this.renderPlayersSheet()}
          `
        : nothing,
      host,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.overlayHost?.remove();
    this.overlayHost = null;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.syncOverlay();
  }

  updated() {
    this.syncOverlay();
  }

  private get currentPlayers(): string[] {
    return this.workingPlayers ?? this.players;
  }

  private get filledCount(): number {
    return this.currentPlayers.filter((p) => p.trim().length > 0).length;
  }

  private get duplicateIndices(): Set<number> {
    return findDuplicateIndices(this.currentPlayers);
  }

  private emit() {
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { players: this.currentPlayers.map((p) => p) },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private updatePlayer(i: number, val: string) {
    this.workingPlayers = this.currentPlayers.map((p, idx) => (idx === i ? val : p));
    this.emit();
    this.requestUpdate();
  }

  private removePlayer(i: number) {
    if (this.currentPlayers.length <= 1) return;
    this.workingPlayers = this.currentPlayers.filter((_, idx) => idx !== i);
    this.emit();
    this.requestUpdate();
  }

  private addPlayer() {
    if (this.currentPlayers.length >= MAX_PLAYERS) return;
    this.workingPlayers = [...this.currentPlayers, ''];
    this.emit();
    this.requestUpdate();
  }

  private closeSheet() {
    this.playersSheetOpen = false;
    this.workingPlayers = null;
    this.requestUpdate();
  }

  render() {
    const count = this.filledCount;
    const label = count === 0 ? '0 Jugadores' : `${count} Jugadores`;
    return html`
      <button
        class="players-trigger"
        type="button"
        @click=${() => {
          this.playersSheetOpen = true;
          this.workingPlayers = [...this.players];
          this.requestUpdate();
        }}
      >
        <div class="row" style="gap: 0.6rem; align-items: center; width: 100%;">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <div style="flex: 1; text-align: left;">
            <div
              style="font-size: 0.78rem; letter-spacing: 0.18em; font-weight: 700; color: var(--muted);"
            >
              JUGADORES
            </div>
            <div style="font-weight: 700; margin-top: 0.1rem;">${label}</div>
          </div>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M7 10l5 5 5-5" />
          </svg>
        </div>
      </button>
    `;
  }

  private renderPlayersSheet() {
    const dupes = this.duplicateIndices;
    const inputRows = this.currentPlayers.map(
      (p, i) => html`
        <div class="row" style="gap: 0.5rem;">
          <button
            class="btn-pill-mini"
            type="button"
            @click=${() => this.removePlayer(i)}
            title="Quitar"
            ?disabled=${this.currentPlayers.length <= 1}
            aria-label="Quitar jugador ${i + 1}"
          >
            ✕
          </button>
          <input
            type="text"
            class=${dupes.has(i) ? 'has-error' : ''}
            style="flex: 1;"
            .value=${p}
            @input=${(e: Event) =>
              this.updatePlayer(i, (e.target as HTMLInputElement).value)}
            placeholder="Jugador ${i + 1}"
            maxlength="20"
          />
        </div>
      `,
    );

    return html`
      <div class="bottom-sheet" role="dialog" aria-label="Configurar jugadores">
        <div class="bottom-sheet-handle"></div>
        <div class="bottom-sheet-header">
          <span class="bottom-sheet-title">Configurar jugadores</span>
          <button
            class="bottom-sheet-close"
            type="button"
            @click=${() => this.closeSheet()}
            aria-label="Cerrar"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="bottom-sheet-content">
          <div class="stack" style="gap: 0.6rem;">
            ${inputRows}
          </div>
          ${dupes.size > 0
            ? html`<p class="error-text" style="margin: 0.5rem 0 0;">
                ⚠ No puede haber nombres repetidos.
              </p>`
            : nothing}
          <button
            class="btn-pill gray"
            type="button"
            style="width: 100%; min-height: 0; padding: 0.6rem 1rem; margin-top: 0.8rem;"
            @click=${() => this.addPlayer()}
            ?disabled=${this.currentPlayers.length >= MAX_PLAYERS}
          >
            <span class="label" style="font-size: 0.95rem;">+ Agregar jugador</span>
          </button>
        </div>
        <div class="bottom-sheet-footer">
          <button
            class="btn-pill primary"
            type="button"
            style="width: 100%;"
            @click=${() => this.closeSheet()}
          >
            <span class="label">LISTO</span>
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-players-config': JgzPlayersConfig;
  }
}
